<template>
  <div
    ref="lyricView"
    class="lyric-view"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd">
    <div
      v-if="lyric"
      ref="lyricWrapper"
      class="lyric-wrapper"
      :style="{
        transform: `translate3d(0, ${nowTranslateY}px, 0)`,
        transition: `${isDragging? 'none':`all ease ${lyricScrollTime}ms`}`
      }">
      <div
        v-for="(item, index) in allLyric"
        ref="lyricLine"
        :key="index"
        :class="{
          [lyricCenterClass]: index === centerLyricIdx,
          [lyricActiveClass]: (index === activeLyricIdx) &&
            !(lyric[index -1] && !lyric[index -1][0])
        }">
        <div>
          <div class="left-col">
            <div class="line-num">{{ index == 0 ? '' : index }}</div>
            <div
              class="start-time"
              :class="{ 'time-unset': lyric[index -1] && !lyric[index -1][0] }">
              {{ item[0] ? getHhMmSsFormat(Math.round(item[0] * 1000)) : '' }}
            </div>
            <div class="center-col">
              <p
                :style="`lineHeight: ${lyricLineheight}`"
                :class="{
                  [lyricHighlightedClass]: index === activeLyricIdx
                }">
                {{ item[1] }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  name: 'LyricScroll',
  props: {
    currentTime: {
      type: Number,
      required: true,
    },
    lyric: {
      type: Array,
      required: true,
    },
    lyricActiveClass: {
      type: String,
      required: true,
    },
    lyricHighlightedClass: {
      type: String,
      required: true,
    },
    lyricCenterClass: {
      type: String,
      required: true,
    },
    // Scroll to the target lyrics time, in ms
    lyricScrollTime: {
      type: Number,
      default: 400,
    },
    dragendWaitTime: {
      type: Number,
      default: 3000,
    },
    lyricMargin: {
      type: String,
      default: '20px',
    },
    lyricLineheight: {
      type: String,
      default: '1.5em',
    },
    triangleColor: {
      type: String,
      default: 'orange',
    },
    triangleWidth: {
      type: String,
      default: '40px',
    },
    centerLineColor: {
      type: String,
      default: '#ccc',
    },
    centerTimeColor: {
      type: String,
      default: 'orange',
    },
  },
  data: () => ({
    startClientY: 0,
    startTranslateY: 0,
    isDragging: false,
    nowTranslateY: 0,
    timer: null,
    centerLyricIdx: -1,
    offsetHeightList: [],
    wrapper: null,
    wrapperHeight: 0,
    viewHeight: 0,
    activeLyricsIdx: 0,
    ccTime: 0,
  }),
  computed: {
    allLyric() {
      // initialize raw data to maintain firstline of preview
      const result = [null, '']
      const lyricsData = JSON.parse(JSON.stringify(this.lyric))
      let flag = 0
      lyricsData.forEach((line) => {
        if (!line[0]) {
          if (lyricsData[flag - 1]) {
            const prevEndTime = lyricsData[flag - 1][0]
            // eslint-disable-next-line no-param-reassign
            line[0] = prevEndTime + 1
          }
        }
        flag += 1
      })
      return [result].concat(lyricsData)
    },
    // OffsetTop of the div corresponding to each lyric
    offsetTopList() {
      const resultArr = []
      let totalHeight = 0
      this.$refs.lyricLine.forEach(line => {
        const height = line.offsetHeight
        this.offsetHeightList.push(height)
        resultArr.push(totalHeight)
        totalHeight += height
      })
      return resultArr
    },

    // Subscript of the currently highlighted lyric
    activeLyricIdx() {
      const { allLyric } = this
      const { currentTime } = this
      // eslint-disable-next-line no-plusplus
      for (let i = 0, len = allLyric.length; i < len; i++) {
        if (
          Math.floor(allLyric[i][0]) <= currentTime
          && ((allLyric[i + 1] && Math.floor(allLyric[i + 1][0]) > currentTime)
            || !allLyric[i + 1])
        ) {
          return i
        }
      }
      return 0
    },

    // Maximum distance of the first lyric from the top of the container
    lyricTopPadding() {
      return this.viewHeight / 2 - this.firstLyricHeight / 2
    },

    // Maximum distance of the last lyric from the bottom of the container
    lyricBottomPadding() {
      return this.viewHeight / 2 - this.lastLyricHeight / 2
    },

    // Minimum value of nowTranslateY, which is the maximum value of the lyrics scrolling up
    minTranslateY() {
      return -(this.wrapperHeight - this.viewHeight + this.lyricBottomPadding)
    },
  },
  watch: {
    currentTime() {
      this.updateLyricPos()
    },
    lyric() {
    },
    allLyric() {
      this.$nextTick(() => {
        this.setFirstLastLyricHeight()
        this.viewHeight = this.$refs.lyricView.offsetHeight
        this.wrapperHeight = this.$refs.lyricWrapper.offsetHeight
        this.nowTranslateY = this.lyricTopPadding
      })
    },
  },
  mounted() {
    this.wrapper = this.$refs.lyricWrapper
  },

  methods: {
    getHhMmSsFormat(time) {
      return this.$parent.msToTime(time)
    },
    unitDivide(unitNum, num) {
      const num1 = parseFloat(unitNum)
      const unit = unitNum.replace(num1, '')
      return `${num1 / num}${unit}`
    },

    // Convert seconds to xx:xx format
    timeToStr(num) {
      // eslint-disable-next-line no-param-reassign
      num = Math.round(num)
      let second = num % 60
      let minute = Math.floor(num / 60)
      if (second < 10) {
        second = `0${second}`
      }
      if (minute < 10) {
        minute = `0${minute}`
      }
      return `${minute}:${second}`
    },

    // Set the lyric height of the first and last sentence
    setFirstLastLyricHeight() {
      const lyricLines = this.$refs.lyricLine
      this.firstLyricHeight = lyricLines[0].offsetHeight
      this.lastLyricHeight = lyricLines[lyricLines.length - 1].offsetHeight
    },

    changeCurrentTime() {
      this.$nextTick(() => {
        this.isDragging = false
        this.$emit('change-current-time', this.allLyric[this.centerLyricIdx][0])
      })
    },

    // Get the translateY of the current element in real time
    getTranslateY(el) {
      const curStyle = window.getComputedStyle(el)
      const curTransform = curStyle.transform || curStyle.webkitTransform
      return curTransform.split(',')[5].replace(')', '')
    },

    // Update the lyrics position
    updateLyricPos() {
      if (!this.isDragging) {
        const { activeLyricIdx } = this
        const scrollTarget = this.offsetTopList[activeLyricIdx]
          + this.offsetHeightList[activeLyricIdx] / 2
          - this.viewHeight / 2
        this.nowTranslateY = -scrollTarget
      }
    },

    // touch event trigger
    onTouchStart(e) {
      this.isDragging = true
      clearTimeout(this.timer)
      this.startClientY = e.touches[0].clientY
      this.startTranslateY = Number(this.getTranslateY(this.wrapper))
      this.nowTranslateY = this.startTranslateY
    },

    onTouchMove(e) {
      e.preventDefault()
      const { clientY } = e.touches[0]
      const targetTranslateY = this.startTranslateY + clientY - this.startClientY
      if (targetTranslateY > this.lyricTopPadding) {
        this.nowTranslateY = this.lyricTopPadding
      } else if (targetTranslateY < this.minTranslateY) {
        this.nowTranslateY = this.minTranslateY
      } else {
        this.nowTranslateY = targetTranslateY
        this.centerLyricIdx = this.findCenterLyricIdx()
      }
    },

    onTouchEnd() {
      this.timer = setTimeout(() => {
        this.isDragging = false
        this.centerLyricIdx = -1
      }, this.dragendWaitTime)
    },

    // Find the lyrics closest to the middle in the current field of view and return the subscript
    // eslint-disable-next-line consistent-return
    findCenterLyricIdx() {
      const halfViewHeight = this.viewHeight / 2
      const { offsetTopList } = this
      const { nowTranslateY } = this

      for (let i = 0, len = offsetTopList.length; i < len; i += 1) {
        const v = offsetTopList[i]
        if (
          (nowTranslateY + v < halfViewHeight
            && offsetTopList[i + 1]
            && nowTranslateY + offsetTopList[i + 1] > halfViewHeight)
          || !offsetTopList[i + 1]
        ) {
          return i
        }
      }
    },
  },
}
</script>
<style lang="scss" scoped>
/* Word cc sync word start */

.lyric-view {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  padding-top: 20px;
  min-height: 400px;
  .center-mark {
    box-sizing: border-box;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
    display: flex;
    align-items: center;

    .triangle {
      border-style: solid;
    }

    .line {
      flex: 1;
      height: 1px;
      margin: 0 20px;
    }
  }
  .time-unset {
    opacity: 0;
  }
  .time-unset-lyric {
    color: #707070 !important;
  }
}

.lyric-wrapper {
  transition: ease 0.3s;
  & > div p {
    padding: 0;
    margin: 0;
  }
}
</style>
